import React, { useState, useRef, useEffect } from 'react';

const ImageCropperModal = ({ file, onCrop, onCancel }) => {
  const canvasRef = useRef(null);
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0, size: 200 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage(img);
      const size = Math.min(img.width, img.height) * 0.8;
      setCrop({
        x: (img.width - size) / 2,
        y: (img.height - size) / 2,
        size: size,
      });
      URL.revokeObjectURL(url);
    };
    img.src = url;
  }, [file]);

  useEffect(() => {
    if (!image || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Calculate display size fitting within 400x400
    const displayWidth = 400;
    const displayHeight = 400;
    canvas.width = displayWidth;
    canvas.height = displayHeight;
    
    const scale = Math.min(displayWidth / image.width, displayHeight / image.height);
    const dx = (displayWidth - image.width * scale) / 2;
    const dy = (displayHeight - image.height * scale) / 2;

    ctx.clearRect(0, 0, displayWidth, displayHeight);
    ctx.drawImage(image, dx, dy, image.width * scale, image.height * scale);

    // Draw dark overlay
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, displayWidth, displayHeight);

    // Clear crop area
    const cx = dx + crop.x * scale;
    const cy = dy + crop.y * scale;
    const cSize = crop.size * scale;
    ctx.clearRect(cx, cy, cSize, cSize);
    ctx.drawImage(
      image,
      crop.x, crop.y, crop.size, crop.size,
      cx, cy, cSize, cSize
    );

    // Draw border
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.strokeRect(cx, cy, cSize, cSize);
  }, [image, crop]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !image) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scale = Math.min(rect.width / image.width, rect.height / image.height);
    
    const dx = (e.clientX - dragStart.x) / scale;
    const dy = (e.clientY - dragStart.y) / scale;

    setCrop(prev => {
      let newX = prev.x + dx;
      let newY = prev.y + dy;
      // Bounds check
      newX = Math.max(0, Math.min(newX, image.width - prev.size));
      newY = Math.max(0, Math.min(newY, image.height - prev.size));
      
      return { ...prev, x: newX, y: newY };
    });
    setDragStart({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleZoom = (e) => {
    const delta = e.deltaY > 0 ? -10 : 10;
    setCrop(prev => {
      let newSize = prev.size + delta;
      newSize = Math.max(50, Math.min(newSize, Math.min(image.width, image.height)));
      
      // Keep centered
      let newX = prev.x - delta / 2;
      let newY = prev.y - delta / 2;
      
      newX = Math.max(0, Math.min(newX, image.width - newSize));
      newY = Math.max(0, Math.min(newY, image.height - newSize));
      
      return { x: newX, y: newY, size: newSize };
    });
  };

  const performCrop = () => {
    if (!image) return;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = crop.size;
    tempCanvas.height = crop.size;
    const ctx = tempCanvas.getContext('2d');
    ctx.drawImage(
      image,
      crop.x, crop.y, crop.size, crop.size,
      0, 0, crop.size, crop.size
    );
    tempCanvas.toBlob((blob) => {
      const croppedFile = new File([blob], file.name, { type: 'image/jpeg' });
      onCrop(croppedFile);
    }, 'image/jpeg', 0.9);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-75">
      <div className="bg-white p-4 rounded-lg shadow-xl w-[450px]">
        <h3 className="text-lg font-bold mb-4">Crop Image (1:1 Ratio Required)</h3>
        {image ? (
          <div className="flex flex-col items-center">
            <canvas
              ref={canvasRef}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onWheel={handleZoom}
              className="cursor-move border border-gray-300"
              style={{ width: '400px', height: '400px', touchAction: 'none' }}
            />
            <p className="text-sm text-gray-500 mt-2">Scroll to zoom, drag to move</p>
            <div className="flex justify-end gap-2 w-full mt-4">
              <button type="button" onClick={onCancel} className="px-4 py-2 border rounded bg-gray-100">Cancel</button>
              <button type="button" onClick={performCrop} className="px-4 py-2 text-white bg-purple-600 rounded hover:bg-purple-700">Crop & Save</button>
            </div>
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </div>
    </div>
  );
};

export default ImageCropperModal;
